import { Button, Checkbox, Heading, Typography } from '@altify/components';
import { useContent } from '@altify/utils';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import ExclamationIcon from 'assets/icons/exclamation_outline_white.svg';
import BackButton from 'components/BackButton/BackButton';
import { CustomLoader } from 'components/CustomLoader/CustomLoader';
import { useProduct } from 'hooks/products/useProducts';
import { useState } from 'react';
import { useGetProductDepositInfo } from 'store/slices/cdn-api';
import { content } from './route.content';

export const Route = createFileRoute(
  '/_authenticated/funds/withdraw/crypto/$productCode/disclaimer/accept',
)({
  component: WithdrawCryptoAccept,
});

function WithdrawCryptoAccept() {
  const navigate = useNavigate();
  const { productCode } = Route.useParams();
  const { product } = useProduct(productCode);
  const { data: depositInfo, isLoading: isDepositInfoLoading } =
    useGetProductDepositInfo(productCode);

  const [accepted, setAccepted] = useState(false);
  const localizedContent = useContent(content);

  return (
    <div className="h-full py-4 px-4 md:px-8">
      <BackButton
        className={
          'w-full text-left lg:bg-body-background mt-1 ml-0 pl-6 lg:-mt-4 pt-2 lg:ml-0 lg:pl-0 lg:pt-0'
        }
      />
      <div className="w-full items-center flex flex-col mb-[80px]">
        <div className="flex flex-col items-center mt-4 lg:mt-14 gap-4 max-w-[600px] w-full">
          <img src={ExclamationIcon} alt="Exclamation Icon" className="w-24" />
          <Heading level={1} className="mb-4">
            {localizedContent.accept}
          </Heading>
          <div className="flex flex-col items-center gap-6 w-full">
            {isDepositInfoLoading ? (
              <div className="mt-12">
                <CustomLoader />
              </div>
            ) : (
              <>
                <div className="flex flex-col rounded-xl bg-background p-8 gap-4 w-full">
                  <div className="space-y-6">
                    <div>
                      <Typography
                        size="large"
                        className="text-neutral-300 font-semibold text-center sm:text-left w-full">
                        {localizedContent.networkWarning.title(
                          depositInfo?.network ?? '',
                        )}
                      </Typography>
                      <Typography
                        size="large"
                        className="text-neutral-300 text-center sm:text-left w-full">
                        {localizedContent.networkWarning.description(
                          product?.name ?? '',
                          depositInfo?.network ?? '',
                        )}
                      </Typography>
                    </div>

                    <div>
                      <Typography
                        size="large"
                        className="text-neutral-300 font-semibold text-center sm:text-left w-full">
                        {localizedContent.addressWarning.title}
                      </Typography>
                      <Typography
                        size="large"
                        className="text-neutral-300 text-center sm:text-left w-full">
                        {localizedContent.addressWarning.description}
                      </Typography>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col rounded-xl bg-background p-8 gap-4 w-full">
                  <Checkbox
                    className="flex flex-row flex-grow justify-start"
                    onChange={value => {
                      setAccepted(value);
                    }}>
                    <Typography size="large" className="ml-2 text-white">
                      {localizedContent.understand}
                    </Typography>
                  </Checkbox>
                </div>
              </>
            )}
            <div className="pb-20 w-full">
              <Button
                size="large"
                color="primary"
                disabled={!accepted}
                onPress={() =>
                  navigate({
                    to: `/funds/withdraw/crypto/${productCode}/wallet/verify`,
                  })
                }
                text={localizedContent.next}
                className="w-full"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
